import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { useClubChallenges, useCreateChallenge } from '@/hooks/challenges'
import { challengePhase, challengeTotal } from '@/lib/challenges'
import { ChallengeFormModal } from './ChallengeFormModal'
import { formatPeriod, PhaseBadge } from './ChallengeMeta'

/** Challenges list on the club page */
export function ClubChallengesSection({ clubId }: { clubId: string }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { data: challenges = [], isLoading } = useClubChallenges(clubId)
  const createChallenge = useCreateChallenge()
  const [creating, setCreating] = useState(false)

  return (
    <Card className="border-0 shadow-sm min-w-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold">🎯 {t('challenges.title')}</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}>
            {t('challenges.create')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-20 animate-pulse rounded bg-muted" />
        ) : challenges.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('challenges.empty')}</p>
        ) : (
          <ul className="space-y-3">
            {challenges.map((challenge) => {
              const total = challengeTotal(challenge, challenge.category_count)
              const percent = total ? Math.round((challenge.my_completed / total) * 100) : 0
              return (
                <li key={challenge.id}>
                  <Link
                    to={`/dashboard/clubs/${clubId}/challenges/${challenge.id}`}
                    className="block rounded-lg border border-border p-4 hover:bg-accent"
                  >
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="min-w-0 truncate font-medium text-foreground">
                        {challenge.name}
                      </p>
                      <PhaseBadge phase={challengePhase(challenge)} />
                    </div>
                    <p className="mb-3 text-xs text-muted-foreground">
                      {formatPeriod(challenge, i18n.language)} ·{' '}
                      {t('challenges.categoryCount', { count: challenge.category_count })}
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-success"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t('challenges.myProgress', { done: challenge.my_completed, total })}
                      </span>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>

      {creating && (
        <ChallengeFormModal
          title={t('challenges.create')}
          isPending={createChallenge.isPending}
          error={createChallenge.isError}
          onClose={() => setCreating(false)}
          onSubmit={async (values) => {
            const challenge = await createChallenge.mutateAsync({ clubId, ...values })
            setCreating(false)
            navigate(`/dashboard/clubs/${clubId}/challenges/${challenge.id}`)
          }}
        />
      )}
    </Card>
  )
}
